import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";

import AdminPanel from "components/admin/adminPanel/AdminPanel";
import SpecimenWarningDialog from "components/admin/editCasePanel/SpecimenWarningDialog";
import HalfScreenPanel from "components/admin/editPanels/halfScreenEditPanel/HalfScreenPanel";
import DjangoLink from "components/DjangoLink";

import SpecimenForm from "./SpecimenForm";

type SpecimensPanelProps = {
  returnUrl: string;
  adminCase: CaseAdminDetail;
  specimenId: number;
  resetErrorMessage: Function;
  resetDeletionStatus: Function;
  canUpdateSpecimen?: boolean;
};

const SpecimensPanel = ({
  returnUrl,
  adminCase,
  specimenId,
  resetErrorMessage,
  resetDeletionStatus,
  canUpdateSpecimen = true,
}: SpecimensPanelProps) => {
  const navigate = useNavigate();

  const [delSpecimen, setDelSpecimen] = useState<boolean>(false);

  const specimen = useMemo(
    () => adminCase.specimens?.find((spec) => spec.id === specimenId),
    [specimenId]
  );

  const handleOnClose = () => {
    navigate(returnUrl, { replace: true });
  };

  useEffect(() => {
    if (adminCase.delStatus === "success" && delSpecimen) {
      setDelSpecimen(false);
      resetDeletionStatus();
    }
  }, [adminCase.delStatus]);

  const canDeleteSpecimen = !adminCase.slides.find(
    (slide) => slide.specimen_id === specimenId
  );

  if (!specimen) return null;

  return (
    <>
      {delSpecimen && (
        <SpecimenWarningDialog
          case_id={adminCase.id!}
          delStatus={adminCase.delStatus}
          errorMessage={adminCase.delErrorMessage}
          resetErrorMessage={resetErrorMessage}
          specimen={specimen}
          onClose={() => setDelSpecimen(false)}
        />
      )}
      <HalfScreenPanel onClose={handleOnClose}>
        <div className="HalfScreen__container">
          <AdminPanel
            title={{
              prefix: `Specimen #${specimen.id}`,
              main: specimen.name,
            }}
            djangoLink={
              <DjangoLink
                route={`histoslide/specimen/${specimen.id}/change/`}
                title="Django Specimen Access"
              />
            }
          >
            <SpecimenForm
              specimen={specimen}
              adminCase={adminCase}
              onClose={handleOnClose}
              canDeleteSpecimen={canDeleteSpecimen}
              setDelSpecimen={setDelSpecimen}
              disableSubmit={!canUpdateSpecimen}
            />
          </AdminPanel>
        </div>
      </HalfScreenPanel>
    </>
  );
};

export default SpecimensPanel;
